import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Check, Heart, Wind, Building2, HelpCircle, Compass } from "lucide-react";
import { DisclaimerShort } from "@/components/Disclaimer";

// Theme images - Compasses, Sunflowers, Maps - cosmic old world feel
const THEME_IMAGES = {
  cosmicSunflower: "https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/yrzxqxw2_image.png",
  truenorthBrand: "https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/anws8vl0_image.png",
  // New cosmic compass star image
  cosmicCompassStar: "https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/jpp7pq4j_image.png",
  sunflowerField: "https://images.unsplash.com/photo-1597848212624-a19eb35e2651?w=800&q=80",
  vintageMap: "https://images.unsplash.com/photo-1612257998095-1feb6bd2bcf9?w=800&q=80",
};

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    "Daily alignment signals",
    "Pattern recognition tools",
    "Weekly reflection system",
    "Transformation tracking"
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#2d4a44' }}>
      {/* Navigation */}
      <nav className="px-4 sm:px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <img 
              src="/compass-logo.png" 
              alt="Compass" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
            />
            <span className="font-heading text-xl sm:text-2xl text-foreground font-bold">Compass</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button 
              variant="ghost" 
              className="text-foreground/70 hover:text-foreground text-xs sm:text-sm px-2 sm:px-4"
              onClick={() => navigate("/login")}
              data-testid="nav-login-btn"
            >
              Sign In
            </Button>
            <Button 
              className="btn-primary text-xs sm:text-sm px-3 sm:px-6"
              onClick={() => navigate("/register")}
              data-testid="nav-register-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="px-4 sm:px-6 pt-8 sm:pt-12 pb-16 sm:pb-24 max-w-7xl mx-auto">
        {/* Hero - Full Cosmic Image with CTA overlay */}
        <div className="relative rounded-2xl overflow-hidden mb-10 sm:mb-14 animate-fade-in">
          <img 
            src={THEME_IMAGES.cosmicCompassStar}
            alt="Navigate Your True North"
            className="w-full h-[500px] sm:h-[500px] md:h-[550px] object-cover object-center"
          />
          {/* Darker gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent" />
          
          {/* Hero Content Overlay - positioned in upper left dark area */}
          <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-center p-5 sm:p-10 md:p-14">
            <div className="max-w-md">
              <h1 className="hero-text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-3 sm:mb-6" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.9)' }}>
                Navigate Your<br />
                True North
              </h1>
              
              <p className="text-white/90 text-sm sm:text-lg max-w-lg mb-4 sm:mb-8">
                Stress is constant. Your response doesn't have to be. Compass helps you pause, recognise your triggers, and choose your next move with clarity.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  className="bg-primary hover:bg-[#6a8e6a] text-base py-5 px-8 rounded-full"
                  onClick={() => navigate("/register")}
                  data-testid="hero-start-btn"
                  style={{ color: '#ffffff', fontWeight: 700 }}
                >
                  <span style={{ color: '#ffffff' }}>Begin Your Journey</span>
                  <ArrowRight className="ml-2 w-5 h-5" style={{ color: '#ffffff', stroke: '#ffffff' }} />
                </Button>
                <Button 
                  variant="outline"
                  className="text-base py-5 px-8 border-white/40 hover:bg-white/10 rounded-full"
                  onClick={() => navigate("/login")}
                  data-testid="hero-signin-btn"
                  style={{ color: '#ffffff', fontWeight: 700 }}
                >
                  <span style={{ color: '#ffffff' }}>Continue Your Path</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Wellness Tools Section */}
        <section className="mb-10 sm:mb-14">
          <h2 className="text-[#a69373] text-lg sm:text-xl font-heading mb-5 text-center animate-fade-in" style={{ animationDelay: "450ms" }}>
            Tools for your wellbeing
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Wellness Check */}
            <Card 
              className="glass-card feature-card overflow-hidden cursor-pointer animate-fade-in"
              style={{ animationDelay: "500ms" }}
              onClick={() => navigate("/wellness-check")}
              data-testid="wellness-check-card"
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-4">
                  <div className="card-icon w-14 h-14 rounded-2xl bg-gradient-to-br from-[#d0dce8] to-[#c8d0e0] flex items-center justify-center shrink-0">
                    <Heart className="w-7 h-7 text-[#5a7090]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[#a69373] font-heading text-lg mb-1">Wellness Check</h3>
                    <p className="text-[#a69373] text-sm">Understand your stress levels</p>
                  </div>
                  <ArrowRight className="card-arrow w-5 h-5 text-[#a69373]" />
                </div>
              </CardContent>
            </Card>

            {/* Breathing Exercise */}
            <Card 
              className="glass-card feature-card overflow-hidden cursor-pointer animate-fade-in"
              style={{ animationDelay: "550ms" }}
              onClick={() => navigate("/breathe")}
              data-testid="breathing-card"
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-4">
                  <div className="card-icon w-14 h-14 rounded-2xl bg-gradient-to-br from-[#d4e8d8] to-[#c8e0d0] flex items-center justify-center shrink-0">
                    <Wind className="w-7 h-7 text-[#5a8a60]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[#a69373] font-heading text-lg mb-1">Breathing Exercise</h3>
                    <p className="text-[#a69373] text-sm">Calm your mind in minutes</p>
                  </div>
                  <ArrowRight className="card-arrow w-5 h-5 text-[#a69373]" />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Daily Compass Feature */}
        <Card className="glass-card feature-card overflow-hidden mb-10 sm:mb-14 cursor-pointer animate-fade-in" style={{ animationDelay: "600ms" }}>
          <CardContent className="p-5 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
              <div className="card-icon w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#7a9e7a] to-[#5a8a5a] flex items-center justify-center shrink-0">
                <Compass className="w-8 h-8 sm:w-10 sm:h-10 force-white" />
              </div>
              <div className="text-center sm:text-left flex-1">
                <h3 className="text-[#a69373] text-xl sm:text-2xl font-heading mb-2">
                  Daily Compass
                </h3>
                <p className="text-[#a69373] text-sm sm:text-base mb-4 max-w-lg">
                  Your daily alignment tool. Receive a signal, take an action, check in with yourself. 
                  Build awareness one day at a time.
                </p>
                <Button 
                  className="btn-primary"
                  onClick={() => navigate("/register")}
                >
                  Start Using Compass
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cosmic Sunflower Feature Image */}
        <div className="relative rounded-2xl overflow-hidden mb-10 sm:mb-14 h-40 sm:h-48 animate-fade-in" style={{ animationDelay: "650ms" }}>
          <img 
            src={THEME_IMAGES.cosmicSunflower}
            alt="Cosmic Sunflower - Growth and transformation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 text-center">
            <p className="text-white/90 text-sm sm:text-base font-heading">Transform your inner landscape</p>
          </div>
        </div>

        {/* Quote */}
        <div className="mt-12 sm:mt-16 text-center max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: "750ms" }}>
          <blockquote className="text-lg sm:text-xl md:text-2xl font-heading text-[#a69373] italic font-bold">
            "The unexamined life is not worth living."
          </blockquote>
          <p className="text-[#a69373] mt-3 text-sm font-bold">— Socrates</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 sm:px-6 py-6 border-t border-black/10">
        <div className="max-w-7xl mx-auto">
          <DisclaimerShort className="text-center mb-4" />
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-[#a69373] text-xs sm:text-sm text-center sm:text-left">
              © 2025 Compass. A space for thoughtful transformation.
            </p>
            <div className="flex gap-4 sm:gap-6">
              <span 
                className="text-[#a69373] hover:text-primary text-xs sm:text-sm transition-colors cursor-pointer"
                onClick={() => navigate("/privacy")}
              >
                Privacy
              </span>
              <span 
                className="text-[#a69373] hover:text-primary text-xs sm:text-sm transition-colors cursor-pointer"
                onClick={() => navigate("/terms")}
              >
                Terms
              </span>
              <span 
                className="text-[#a69373] hover:text-primary text-xs sm:text-sm transition-colors cursor-pointer"
                onClick={() => navigate("/faq")}
              >
                FAQ
              </span>
              <a 
                href="mailto:info@truenorth-zone.com" 
                className="text-[#a69373] hover:text-primary text-xs sm:text-sm transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
